'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { automationDeleteConfirmationMessages, automationTitleFallbackMessage, } from '#pplx/frontend/apps/ask/automations-shared/automationActionMessages.js';
import { AutomationUnreadIndicator } from '#pplx/frontend/apps/ask/automations-shared/AutomationUnreadIndicator.js';
import { AUTOMATIONS_ICON } from '#pplx/frontend/apps/ask/automations-shared/constants.js';
import { getPauseOrResumeIcon, RUN_NOW_ICON, } from '#pplx/frontend/apps/ask/automations-shared/scheduledTaskActionIcons.js';
import { sidebarSessionSelectedEvent } from '#pplx/frontend/apps/ask/events/sidebar/analytics.js';
import { optimisticallyMarkSidebarItemRead } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/sidebar-recent-items-state/sidebarRecentItemsState.js';
import { useMarkThreadRead } from '#pplx/frontend/apps/ask/hooks/threads-hooks/useMarkThreadRead.js';
import { SessionItem } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/SessionItem.js';
import { getAutomationSidebarPath, isAutomationRoutePath, } from '#pplx/frontend/apps/ask/queries/automation-queries/automationRunRoute.js';
import { useAutomationActions } from '#pplx/frontend/apps/ask/queries/automation-queries/useAutomationActions.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
function resolveAutomationItem(props) {
    if (props.source === 'recent') {
        return {
            item: props.item,
            automation: {
                automation_id: props.item.automationId,
                name: props.item.title,
                state: props.item.automationState,
            },
            isUnread: Boolean(props.item.unread),
            href: props.item.link,
            isPinned: props.isPinned,
            loading: props.item.isStreaming,
            selected: undefined,
            showDetailsAction: true,
            showTypeIcon: true,
            projectTitle: props.item.projectTitle,
            projectIcon: props.item.projectIcon,
            answerPreview: props.item.answer_preview,
            updatedAt: props.item.updated_at,
            onSelect: undefined,
            surface: 'sidebarRecent',
        };
    }
    return {
        item: undefined,
        automation: props.automation,
        isUnread: props.isUnread ?? false,
        href: props.href,
        isPinned: props.isPinned ?? true,
        loading: props.loading,
        selected: props.selected,
        showDetailsAction: Boolean(props.href && props.href !== getAutomationSidebarPath(props.automation.automation_id)),
        showTypeIcon: props.showTypeIcon,
        projectTitle: props.projectTitle,
        projectIcon: props.projectIcon,
        answerPreview: props.answerPreview,
        updatedAt: props.updatedAt,
        onSelect: props.onSelect,
        surface: 'sidebarPinned',
    };
}
const automationActionSelectedEvent = EVENT_PAYLOAD;
const automationSelectedEvent = EVENT_PAYLOAD;
const automationMenuMessages = defineMessages({
    actions: {
        defaultMessage: 'Automation actions',
        description: 'Accessible label for the button that opens a menu with automation actions',
        id: 'c01fe0882b',
    },
    run: {
        defaultMessage: 'Run now',
        description: 'Sidebar menu item to run a pinned automation immediately',
        id: 'a4189a651e',
    },
    pause: {
        defaultMessage: 'Pause',
        description: 'Sidebar menu item to pause a pinned automation',
        id: '90252838cc',
    },
    enable: {
        defaultMessage: 'Enable',
        description: 'Sidebar menu item to enable a paused pinned automation',
        id: 'd86e123fe3',
    },
    unpin: {
        defaultMessage: 'Unpin',
        description: 'Sidebar menu item to unpin a pinned automation',
        id: '9de60fdcb8',
    },
    pin: {
        defaultMessage: 'Pin',
        description: 'Sidebar menu item to pin an automation',
        id: 'ae7709891e',
    },
    details: {
        defaultMessage: 'See details',
        description: 'Sidebar menu item to open an automation details page',
        id: 'ad5714bc7d',
    },
    delete: {
        defaultMessage: 'Delete',
        description: 'Sidebar menu item to delete a pinned automation',
        id: '187671b797',
    },
});
function getAutomationItemAccessory({ groupedHistoryEnabled, isUnread, showTypeIcon, }) {
    if (isUnread)
        return _jsx(AutomationUnreadIndicator, { size: "sm" });
    if (!groupedHistoryEnabled && !showTypeIcon)
        return undefined;
    return (_jsx("span", { className: "inline-flex opacity-50", children: _jsx(Icon, { icon: AUTOMATIONS_ICON, size: "small", color: "ultraLight", "aria-hidden": true }) }));
}
export function AutomationItem(props) {
    const { isSidebarOpen, isInPopover, collapsed, foreground, groupedHistoryEnabled } = props;
    const { item, automation, isUnread, href, isPinned, loading, selected, showDetailsAction, showTypeIcon, answerPreview, projectTitle, projectIcon, updatedAt, onSelect, surface, } = resolveAutomationItem(props);
    const { $t } = useIntl();
    const { track } = useInstrumentation();
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const detailsHref = getAutomationSidebarPath(automation.automation_id);
    const resolvedHref = href ?? detailsHref;
    const isSelected = selected ??
        // The automation-page link carries its ID in the query string, which
        // pathname matching cannot see.
        (href == null || href === detailsHref
            ? searchParams.get('automationId') === automation.automation_id ||
                isAutomationRoutePath(pathname, automation.automation_id)
            : undefined);
    const title = automation.name.trim() || $t(automationTitleFallbackMessage);
    const isPaused = automation.state.toLowerCase() === 'paused';
    const { runNow, pauseOrResume, togglePin, deleteAutomation } = useAutomationActions({
        automationId: automation.automation_id,
        isPaused,
        isPinned,
        title,
    });
    const trackAction = useCallback((action) => {
        track(automationActionSelectedEvent({
            name: 'computer automation action selected',
            data: {
                automationId: automation.automation_id,
                isLegacy: false,
                state: automation.state,
                triggerKind: null,
                action,
                surface,
            },
        }));
    }, [automation.automation_id, automation.state, surface, track]);
    const handlePinnedSelect = useCallback(() => {
        track(automationSelectedEvent({
            name: 'computer automation selected',
            data: {
                automationId: automation.automation_id,
                isLegacy: false,
                state: automation.state,
                triggerKind: null,
                hasSearch: false,
                surface: 'sidebarPinned',
            },
        }));
    }, [automation.automation_id, automation.state, track]);
    const { markThreadOpenedFromList } = useMarkThreadRead({
        reason: 'sidebar automation item',
    });
    const handleRecentSelect = useCallback(() => {
        if (!item)
            return;
        const contextUUID = item.context_uuid ?? undefined;
        if (contextUUID && item.unread)
            optimisticallyMarkSidebarItemRead(contextUUID);
        track(sidebarSessionSelectedEvent({
            name: 'sidebar session selected',
            data: {
                section: 'history',
                threadType: 'computer',
                itemType: 'automation',
                automationId: item.automationId,
                contextUUID,
                collectionUUID: item.collection_uuid ?? undefined,
            },
        }));
        markThreadOpenedFromList({ contextUUID, unread: item.unread });
    }, [item, markThreadOpenedFromList, track]);
    const handleSelect = item ? handleRecentSelect : (onSelect ?? handlePinnedSelect);
    const handleTogglePin = useCallback(() => {
        trackAction(isPinned ? 'unpin' : 'pin');
        togglePin();
    }, [isPinned, togglePin, trackAction]);
    const handleRun = useCallback(() => {
        trackAction('run');
        runNow();
    }, [runNow, trackAction]);
    const handlePauseOrResume = useCallback(() => {
        trackAction(isPaused ? 'resume' : 'pause');
        pauseOrResume();
    }, [isPaused, pauseOrResume, trackAction]);
    const handleDelete = useCallback(() => {
        trackAction('delete');
        deleteAutomation();
    }, [deleteAutomation, trackAction]);
    const handleSeeDetails = useCallback(() => {
        trackAction('details');
        router.push(detailsHref);
    }, [detailsHref, router, trackAction]);
    const menuItems = useMemo(() => [
        {
            text: isPinned ? $t(automationMenuMessages.unpin) : $t(automationMenuMessages.pin),
            icon: isPinned ? $icon('pinned-off') : $icon('pin'),
            onClick: handleTogglePin,
        },
        {
            text: $t(automationMenuMessages.run),
            icon: RUN_NOW_ICON,
            onClick: handleRun,
        },
        {
            text: isPaused ? $t(automationMenuMessages.enable) : $t(automationMenuMessages.pause),
            icon: getPauseOrResumeIcon(isPaused ? 'resume' : 'pause'),
            onClick: handlePauseOrResume,
        },
        ...(showDetailsAction
            ? [
                {
                    text: $t(automationMenuMessages.details),
                    icon: $icon('arrow-up-right'),
                    onClick: handleSeeDetails,
                },
            ]
            : []),
        {
            text: $t(automationMenuMessages.delete),
            icon: $icon('trash'),
            type: 'destructive',
            onConfirm: handleDelete,
            confirmation: {
                title: $t(automationDeleteConfirmationMessages.title, { title }),
                bodyText: $t(automationDeleteConfirmationMessages.bodyWithThreads),
                confirmButtonTitle: $t(automationDeleteConfirmationMessages.confirm),
            },
        },
    ], [
        $t,
        handleDelete,
        handlePauseOrResume,
        handleRun,
        handleSeeDetails,
        handleTogglePin,
        isPaused,
        isPinned,
        showDetailsAction,
        title,
    ]);
    const accessory = getAutomationItemAccessory({
        groupedHistoryEnabled,
        isUnread,
        showTypeIcon,
    });
    return (_jsx(SessionItem, { kind: "automation", href: resolvedHref, title: title, sidebarOpen: isSidebarOpen, selected: isSelected, isInPopover: isInPopover, collapsed: collapsed, foreground: foreground, loading: loading, menuLabel: $t(automationMenuMessages.actions), menuItems: menuItems, onSelect: handleSelect, accessory: accessory, hoverDetails: {
            updatedAt,
            answerPreview,
            projectTitle,
            projectIcon,
            unread: isUnread,
            onTitleClick: handleSelect,
        } }));
}
//# sourceMappingURL=AutomationItem.js.map